import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";

import { apiSlice } from "../api/apiSlice";

// We use an EntityAdapter to normalize the data structure in our local state.
// It helps us manipulate and query the data in the store and therefore lessens
// the number of rounds we make it back to the server to either fetch or post data e.t.c
// For more information on createEntityAdapter, check out
// https://redux-toolkit.js.org/api/createEntityAdapter
const cvsAdapter = createEntityAdapter({
  // Because EntityAdapter uses ids and Entities, we tell it explicitly where to find the Ids.
  // And they will be on the _id property of data coming from MongoDB
  selectId: (data) => data._id,
});

// Initalize an empty state for our data in store
const initialState = cvsAdapter.getInitialState();

// Extend and add different endpoints to the main ApiSlice
export const extendedApiSlice = apiSlice.injectEndpoints({
  // This endpoint fetches all the cvs of the current logged in user
  endpoints: (builder) => ({
   getCandidateCvs: builder.query({
  // This endpoint fetches all CVs belonging to a specific candidate by their owner_id
  query: (owner_id) => ({
    url: `/cv/?owner=${owner_id}`,
  }),
  
  // We can then intermediately transform the data in a format how we want it to be stored in the store cache
  // In this instance, we use the createEntityAdapter described in line 9 above to normalize the data that
  // has come in to a state that we want it to be.
  transformResponse: (responseData) => {
    // Defensive handling for different API response structures
    const cvArray = Array.isArray(responseData)
      ? responseData
      : (responseData?.data || responseData?.cvs || []);

    console.log('CVs from API:', cvArray); // Debug log to verify data structure

    // Use the EntityAdapter to normalize the array into the Redux state format
    // This creates a normalized structure with 'ids' and 'entities' for efficient lookups
    return cvsAdapter.setAll(initialState, cvArray);
  },

  // ProvideTags and subsequently invalidateTags help in auto-refetching of data when an entity is invalidated
  // This implementation provides granular cache invalidation:
  // - A 'LIST' tag for the entire collection (useful for invalidating all CVs at once)
  // - Individual tags for each CV by its id (useful for invalidating specific CVs)
  providesTags: (result) =>
    result
      ? [
          { type: 'Cv', id: 'LIST' }, // Tag for the entire CV list
          ...result.ids.map(id => ({ type: 'Cv', id })) // Individual tags for each CV
        ]
      : [{ type: 'Cv', id: 'LIST' }], // Fallback if result is null/undefined
}),


    // This endpoint fetches data for a specific CV. Note however, that we can also
    // Go via the createEntityAdapter to get the specific CV by its id.
    // Its just a matter of preference. However, i prioritized data from the EntityAdapter,
    // because it has no attachment to the server.

    getCvById: builder.query({
      query: (id) => ({
        url: `/cv/${id}`,
      }),
      transformResponse: (responseData) => {
        return cvsAdapter.setAll(initialState, responseData);
      },
      providesTags: (result, error, arg) => [
        ...result.ids.map((id) => ({ type: "Cv", id })),
      ],
    }),

    getSingleCv: builder.query({
      query: (id) => ({
        url: `/cv/getsinglecv/${id}`,
      }),
      transformResponse: (responseData) => {
        return responseData; // Don't use setAll for single item
      },
      providesTags: (result, error, arg) => [
        { type: "Cv", id: arg }
      ],
    }),

    addNewCV: builder.mutation({
      query: (newCv) => ({
        url: "/cv",
        method: "POST",
        body: { ...newCv },
      }),
      invalidatesTags: [{ type: "Cv", id: "LIST" }],
    }),

    deleteCv: builder.mutation({
      query: (id) => ({
        url: `/cv/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cv"],
    }),

    editCvSummary: builder.mutation({
      query: ({ cvId, summary }) => ({
        url: `/cv/summary/${cvId}`,
        method: "PATCH",
        body: { summary },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvPortfolio: builder.mutation({
      query: ({ cvId, portfolio }) => ({
        url: `/cv/portfolio/${cvId}`,
        method: "PATCH",
        body: {
          portfolio,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvGitRepo: builder.mutation({
      query: ({ cvId, gitRepo }) => ({
        url: `/cv/gitRepo/${cvId}`,
        method: "PATCH",
        body: {
          gitRepo,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewCvCertificate: builder.mutation({
      query: ({ cvId, certs }) => ({
        url: `/cv/addcert/${cvId}`,
        method: "PATCH",
        body: {
          certs,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    deleteCvCertificate: builder.mutation({
      query: ({ cvId, certId }) => ({
        url: `/cv/deletecert/${cvId}/${certId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvCertificate: builder.mutation({
      query: ({ cvId, certs, certId }) => ({
        url: `/cv/editcert/${cvId}/${certId}`,
        method: "PATCH",
        body: {
          certs,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewTechExperience: builder.mutation({
      query: ({
        cvId,
        operatingSystems,
        databases,
        integrations,
        frameworks,
        otherTechnicalSkills,
        description,
      }) => ({
        url: `/cv/addtech/${cvId}`,
        method: "PATCH",
        body: {
          //techs
          operatingSystems,
          databases,
          integrations,
          frameworks,
          otherTechnicalSkills,

          // Explain about their competence in the selected group
          description,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),
    deleteTechExperience: builder.mutation({
      query: ({ cvId, techId }) => ({
        url: `/cv/deletetech/${cvId}/${techId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),
    editCvTechExperience: builder.mutation({
      query: ({ cvId, techId }) => ({
        url: `/cv/edittech/${cvId}/${techId}`,
        method: "PATCH",
      }),
      // invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewCourse: builder.mutation({
      query: ({ cvId, coursee }) => ({
        url: `/cv/addcourse/${cvId}`,
        method: "PATCH",
        body: {
          coursee,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvCourse: builder.mutation({
      query: ({ cvId, courseId, coursee }) => ({
        url: `/cv/editcourse/${cvId}/${courseId}`,
        method: "PATCH",
        body: {
          coursee,
        },
      }),
      // invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),
    deleteCvCourse: builder.mutation({
      query: ({ cvId, courseeId }) => ({
        url: `/cv/deletecourse/${cvId}/${courseeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewProject: builder.mutation({
      query: ({ cvId, projs }) => ({
        url: `/cv/addproject/${cvId}`,
        method: "PATCH",
        body: {
          projs,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),
    editCvProject: builder.mutation({
      query: ({ cvId, projectId, projs }) => ({
        url: `/cv/editproject/${cvId}/${projectId}`,
        method: "PATCH",
        body: {
          projs,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
    }),
    deleteCvProject: builder.mutation({
      query: ({ cvId, projId }) => ({
        url: `/cv/deleteproject/${cvId}/${projId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewLanguage: builder.mutation({
      query: ({ cvId, langs }) => ({
        url: `/cv/addlanguage/${cvId}`,
        method: "PATCH",
        body: {
          langs,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),
    editCvLanguage: builder.mutation({
      query: ({ cvId, langId, langs }) => ({
        url: `/cv/editlanguage/${cvId}/${langId}`,
        method: "PATCH",
        body: {
          langs,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
    }),
    deleteCvLanguage: builder.mutation({
      query: ({ cvId, langId }) => ({
        url: `/cv/deletelanguage/${cvId}/${langId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewTechSkills: builder.mutation({
      query: ({ cvId, techSkills }) => ({
        url: `/cv/addtechskills/${cvId}`,
        method: "PATCH",
        body: {
          techSkills,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvTechSkills: builder.mutation({
      query: ({ cvId, techSkillsId, techSkills }) => ({
        url: `/cv/edittechskills/${cvId}/${techSkillsId}`,
        method: "PATCH",
        body: {
          techSkills,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
    }),

    deleteCvTechSkills: builder.mutation({
      query: ({ cvId, techSkillsId }) => ({
        url: `/cv/deletetechskills/${cvId}/${techSkillsId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewMethod: builder.mutation({
      query: ({ cvId, method }) => ({
        url: `/cv/addmethod/${cvId}`,
        method: "PATCH",
        body: {
          method,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvMethod: builder.mutation({
      query: ({ cvId, methodId, method }) => ({
        url: `/cv/editmethod/${cvId}/${methodId}`,
        method: "PATCH",
        body: {
          method,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
    }),
    deleteCvMethod: builder.mutation({
      query: ({ cvId, methodId }) => ({
        url: `/cv/deletemethod/${cvId}/${methodId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewEducation: builder.mutation({
      query: ({ cvId, education }) => ({
        url: `/cv/addeducation/${cvId}`,
        method: "PATCH",
        body: {
          education,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvEducation: builder.mutation({
      query: ({ cvId, educationId, education }) => ({
        url: `/cv/editeducation/${cvId}/${educationId}`,
        method: "PATCH",
        body: {
          education,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
    }),
    deleteCvEducation: builder.mutation({
      query: ({ cvId, educationId }) => ({
        url: `/cv/deleteeducation/${cvId}/${educationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewExperience: builder.mutation({
      query: ({ cvId, experiencee }) => ({
        url: `/cv/addexperience/${cvId}`,
        method: "PATCH",
        body: {
          experiencee,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvExperience: builder.mutation({
      query: ({ cvId, experienceId, experience }) => ({
        url: `/cv/editexperience/${cvId}/${experienceId}`,
        method: "PATCH",
        body: {
          experience,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "CV", id: arg._id }],
    }),



    deleteCvExperience: builder.mutation({
      query: ({ cvId, experienceeId }) => ({
        url: `/cv/deleteexperience/${cvId}/${experienceeId}`,

        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewCareerBreak: builder.mutation({
      query: ({ cvId, careerBreak }) => ({
        url: `/cv/addcareerbreak/${cvId}`,
        method: "PATCH",
        body: {
          careerBreak,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    deleteCareerBreak: builder.mutation({
      query: ({ cvId, careerBreakId }) => ({
        url: `/cv/deletecareerbreak/${cvId}/${careerBreakId}`,

        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewQaSkills: builder.mutation({
      query: ({ cvId, qaSkill }) => ({
        url: `/cv/addqaskills/${cvId}`,
        method: "PATCH",
        body: {
          qaSkill,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvQaSkills: builder.mutation({
      query: ({ cvId, qaSkillsId, qaSkill }) => ({
        url: `/cv/editqaskills/${cvId}/${qaSkillsId}`,
        method: "PATCH",
        body: {
          qaSkill,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    deleteQASkills: builder.mutation({
      query: ({ cvId, qaSkillsId }) => ({
        url: `/cv/deleteqaskills/${cvId}/${qaSkillsId}`,

        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewExpertise: builder.mutation({
      query: ({ cvId, expertise }) => ({
        url: `/cv/addexpertise/${cvId}`,
        method: "PATCH",
        body: {
          expertise,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvExpertise: builder.mutation({
      query: ({ cvId, expertise, expertiseId }) => ({
        url: `/cv/addexpertise/${cvId}${expertiseId}`,
        method: "PATCH",
        body: {
          expertise,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    addNewRole: builder.mutation({
      query: ({ cvId, roles }) => ({
        url: `/cv/addroles/${cvId}`,
        method: "PATCH",
        body: {
          roles,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    deleteExpertise: builder.mutation({
      query: ({ cvId, expertiseId }) => ({
        url: `/cv/deleteexpertise/${cvId}/${expertiseId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),


    deleteRole: builder.mutation({
      query: ({ cvId, roleId }) => ({
        url: `/cv/deleteroles/${cvId}/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

    editCvRole: builder.mutation({
      query: ({ cvId, mainroles }) => ({
        url: `/cv/editmainroles/${cvId}`,
        method: "PATCH",
        body: {
          mainroles,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cv", id: arg._id }],
    }),

  }),


});



export const {
  useGetCandidateCvsQuery,
  useGetCvByIdQuery,
  useAddNewCVMutation,
  useDeleteCvMutation,
  useEditCvSummaryMutation,
  useEditCvPortfolioMutation,
  useEditCvGitRepoMutation,
  useAddNewCvCertificateMutation,
  useDeleteCvCertificateMutation,
  useEditCvCertificateMutation,
  useAddNewTechExperienceMutation,
  useDeleteTechExperienceMutation,
  useEditCvTechExperienceMutation,
  useAddNewCourseMutation,
  useEditCvCourseMutation,
  useDeleteCvCourseMutation,
  useAddNewProjectMutation,
  useEditCvProjectMutation,
  useDeleteCvProjectMutation,
  useAddNewLanguageMutation,
  useEditCvLanguageMutation,
  useDeleteCvLanguageMutation,
  useAddNewTechSkillsMutation,
  useEditCvTechSkillsMutation,
  useDeleteCvTechSkillsMutation,
  useAddNewMethodMutation,
  useEditCvMethodMutation,
  useDeleteCvMethodMutation,
  useAddNewExperienceMutation,
  useDeleteCvExperienceMutation,
  useEditCvExperienceMutation,
  useAddNewEducationMutation,
  useEditCvEducationMutation,
  useDeleteCvEducationMutation,
  useAddNewCareerBreakMutation,
  useDeleteCareerBreakMutation,
  useAddNewQaSkillsMutation,
  useEditCvQaSkillsMutation,
  useDeleteQASkillsMutation,
  useAddNewExpertiseMutation,
  useDeleteExpertiseMutation,
  useEditCvExpertiseMutation,
  useAddNewRoleMutation,
  useDeleteRoleMutation,
  useEditCvRoleMutation,
  useGetSingleCvQuery,
  useGetUserLandingPageQuery,
  useUpdateUserImageStateMutation,
} = extendedApiSlice;

export const selectCandidateCvsResult =
  extendedApiSlice.endpoints.getCandidateCvs.select();

const selectCandidateCVsData = createSelector(
  selectCandidateCvsResult,
  (cvsResult) => cvsResult.data
);

// These methods are available automatically from createEntityAdapter.
// We use aliases to name them in what we want. But ultimately not using aliases doesnt hurt at all
export const {
  selectAll: selectAllCandidateCvs,
  selectById: selectCandidateCvByCvId,
  selectIds: selectCandidateCvIds,
  selectTotal: selectItemsInCvAndGiveTheTotal,
} = cvsAdapter.getSelectors(
  (state) => selectCandidateCVsData(state) ?? initialState
);
